import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import type { UserOrganisation } from '../types/types';
import {
  Badge,
  Button,
  Container,
  Table,
  Modal,
  ToastContainer,
  Toast,
  Form,
  Tabs,
  Tab,
} from 'react-bootstrap';
import AssessmentScoreBar from '../components/assessment/AssessmentScoreBar';
import { getTotalScorePercentage, isUndefined } from '../util/util';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
// import { useSupplierReports } from '../hooks/useSupplierReports';
import { useSupplierAssessments } from '../hooks/useSupplierAssessments';
import SupplierBadgeEmbedModal from '../components/supplier/SupplierBadgeEmbedModal';
import { captureSupabaseError } from '../lib/sentry';
import { tracking } from '../util/tracking';
import { gToastPosition, gToastSentiment } from '../util/constst';

const SESSION_STORAGE_KEY = 'assessmentsActiveTab';
const VALID_TABS = new Set(['assigned', 'selfAssessments']);

const SupplierAssessments: React.FC = () => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalMode, setModalMode] = useState<'publish' | 'unpublish'>(
    'publish',
  );
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<
    string | null
  >(null);
  const [toastNotification, setToastNotification] = useState<
    string | undefined
  >(undefined);
  const [processing, setProcessing] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>(() => {
    const urlTab = new URLSearchParams(window.location.search).get('tab');
    if (urlTab && VALID_TABS.has(urlTab)) {
      return urlTab;
    }

    return sessionStorage.getItem(SESSION_STORAGE_KEY) || 'assigned';
  });
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [embedAssessmentId, setEmbedAssessmentId] = useState<string | null>(
    null,
  );
  const [embedReportId, setEmbedReportId] = useState<string | null>(null);

  const navigate = useNavigate();
  const selectedOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected,
  ) as UserOrganisation | undefined;

  // 2. USE THE NEW HOOK WITH SIMPLIFIED, INTENDED PARAMETERS
  // const { data, loading, refetch } = useSupplierReports(
  //   selectedOrganisation?.organisation?.id, // organisationId
  //   true // completedOnly: Always true for reports view
  //   // Note: getSelfAssessments is now TRUE by default in the component's context
  // );

  const {
    assessments: data,
    isLoading: loading,
    refetch,
  } = useSupplierAssessments(selectedOrganisation?.organisation?.id);

  const handleViewAssessment = (assessmentId: string | undefined) => {
    if (!assessmentId) return;
    navigate(`/assessmentResult/${assessmentId}`);
  };

  // ⭐ Open publish modal
  const handleOpenPublishModal = (assessmentId: string) => {
    setSelectedAssessmentId(assessmentId);
    setModalMode('publish');
    setShowConfirmModal(true);
  };

  // ⭐ Open unpublish modal
  const handleOpenUnpublishModal = (assessmentId: string) => {
    setSelectedAssessmentId(assessmentId);
    setModalMode('unpublish');
    setShowConfirmModal(true);
  };

  const handleCloseModal = () => {
    setShowConfirmModal(false);
    setSelectedAssessmentId(null);
  };

  // ⭐ Confirm publish
  const handleConfirmPublish = async () => {
    if (!selectedAssessmentId) return;
    tracking.log('UI', 'assessment_report_publish', {
      assessmentId: selectedAssessmentId,
    });

    try {
      setProcessing(true);

      const { error } = await supabase.functions.invoke('publish-report', {
        body: {
          assessment_id: selectedAssessmentId,
          supplier_id: selectedOrganisation?.organisation?.id,
        },
      });

      if (error) throw error;

      setToastNotification('Report published successfully!');
      await refetch();
    } catch (err) {
      captureSupabaseError(err, { op: 'report.publish' });
      console.error(err);
      setToastNotification('Failed to publish report.');
    }

    setProcessing(false);
    handleCloseModal();
  };

  // ⭐ Confirm unpublish
  const handleConfirmUnpublish = async () => {
    if (!selectedAssessmentId) return;
    tracking.log('UI', 'assessment_report_unpublish', {
      assessmentId: selectedAssessmentId,
    });

    try {
      setProcessing(true);

      const { error } = await supabase
        .from('published_reports')
        .delete()
        .eq('assessment_id', selectedAssessmentId);

      if (error) throw error;

      setToastNotification('Report unpublished successfully!');
      await refetch();
    } catch (err) {
      captureSupabaseError(err, { op: 'report.unpublish' });
      console.error(err);
      setToastNotification('Failed to unpublish report.');
    }

    setProcessing(false);
    handleCloseModal();
  };

  const handleConfirmModal = () => {
    if (modalMode === 'publish') return handleConfirmPublish();
    return handleConfirmUnpublish();
  };

  const getStatusColumn = (status?: string) => {
    if (!status) {
      return <Badge bg="muted">Unknown</Badge>;
    }
    switch (status) {
      case 'completed':
        return <Badge bg="success">Completed</Badge>;
      case 'created':
        return <Badge bg="primary">Created</Badge>;
      case 'started':
        return <Badge bg="warning">Started</Badge>;
      default:
        return <Badge bg="muted">{status}</Badge>;
    }
  };

  const handleOpenBadgeModal = (assessmentId: string | undefined) => {
    if (!assessmentId) return;
    setEmbedAssessmentId(assessmentId);
    const assessment = data.find((a) => a.assessment_id === assessmentId);
    const publishedReportId = assessment?.published_report?.id;

    if (!publishedReportId) {
      setToastNotification('No published report found for this assessment.');
      return;
    }
    setEmbedReportId(publishedReportId);
    setShowBadgeModal(true);
  };

  const togglePublishStatus = (
    assessmentId: string | undefined,
    isPublished: boolean,
  ) => {
    if (!assessmentId) return;
    if (isPublished) {
      handleOpenUnpublishModal(assessmentId);
    } else {
      handleOpenPublishModal(assessmentId);
    }
  };

  const handleCopyPublicUrl = async (assessmentId: string | undefined) => {
    if (!assessmentId) return;
    const assessment = data.find((a) => a.assessment_id === assessmentId);
    const publishedReportId = assessment?.published_report?.id;

    if (!publishedReportId) {
      setToastNotification('No published report found.');
      return;
    }

    const publicUrl = `${window.location.origin}/public/report/${publishedReportId}`;
    navigator.clipboard.writeText(publicUrl);
    setToastNotification('Public URL copied to clipboard!');
  };

  const assignedAssessments = data
    .filter((a) => a.is_self_assessment === false)
    .sort((a, b) => {
      const da = a.created_at ? new Date(a.created_at).getTime() : 0;
      const db = b.created_at ? new Date(b.created_at).getTime() : 0;
      return db - da;
    });

  const selfAssessments = data
    .filter((a) => a.is_self_assessment === true)
    .sort((a, b) => {
      const da = a.created_at ? new Date(a.created_at).getTime() : 0;
      const db = b.created_at ? new Date(b.created_at).getTime() : 0;
      return db - da;
    });

  return (
    <Container fluid>
      <h3>Assessments</h3>
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => {
          if (k && VALID_TABS.has(k)) {
            setActiveTab(k);
            sessionStorage.setItem(SESSION_STORAGE_KEY, k);
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.set('tab', k);
            navigate(
              {
                pathname: window.location.pathname,
                search: `?${searchParams.toString()}`,
              },
              { replace: true },
            );
          }
        }}
        className="mb-3"
      >
        <Tab eventKey="assigned" title="Assigned to me">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <div>
              {assignedAssessments.length === 0 ? (
                <div
                  className="d-flex justify-content-center align-items-center flex-column"
                  style={{ height: '400px' }}
                >
                  <p>No assessments assigned to you.</p>
                  <p>
                    Ask your buyers to assign assessments to you to see them
                    here.
                  </p>
                </div>
              ) : (
                <Table
                  striped
                  bordered
                  className="table-cell-vertical-align-center"
                >
                  <thead>
                    <tr>
                      <th>Assessment</th>
                      <th>Assigned by</th>
                      <th>Status</th>
                      <th>Result</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignedAssessments.map((assessment) => (
                      <tr key={assessment.assessment_id}>
                        <td>
                          {assessment.template_title}{' '}
                          {!assessment.is_report_published &&
                          assessment.is_self_assessment ? (
                            <span className="text-muted">(Unpublished)</span>
                          ) : null}
                        </td>
                        <td>
                          {assessment.assigning_organisation_name || 'N/A'}
                        </td>
                        <td>{getStatusColumn(assessment.status)}</td>
                        <td>
                          {assessment.status === 'completed' && (
                            <AssessmentScoreBar
                              value={getTotalScorePercentage(
                                assessment.total_score,
                                assessment.max_score,
                              )}
                            />
                          )}
                        </td>
                        <td>
                          {assessment.created_at
                            ? new Date(
                                assessment.created_at,
                              ).toLocaleDateString()
                            : '-'}
                        </td>
                        <td>
                          {assessment.status === 'completed' && (
                            <Button
                              variant="link"
                              className="p-0 ms-2"
                              size="sm"
                              onClick={() =>
                                handleViewAssessment(assessment.assessment_id)
                              }
                            >
                              Report
                            </Button>
                          )}
                          {(assessment.status === 'created' ||
                            assessment.status === 'started') && (
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() =>
                                (() => {
                                  tracking.log('UI', 'assessment_start', {
                                    assessmentId: assessment.assessment_id,
                                    action:
                                      assessment.status === 'created'
                                        ? 'start'
                                        : 'continue',
                                    source: 'assigned',
                                  });
                                  navigate(
                                    `/assessment/${assessment.assessment_id}`,
                                  );
                                })()
                              }
                            >
                              {assessment.status === 'created'
                                ? 'Start'
                                : 'Continue'}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              {/* ⭐ Shared Publish/Unpublish Confirmation Modal */}
              <Modal show={showConfirmModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                  <Modal.Title>
                    {modalMode === 'publish'
                      ? 'Publish Report'
                      : 'Unpublish Report'}
                  </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                  {modalMode === 'publish'
                    ? 'Are you sure you want to publish this report? Once published, it will be publicly accessible.'
                    : 'Are you sure you want to unpublish this report? It will no longer be publicly accessible.'}
                </Modal.Body>

                <Modal.Footer>
                  <Button
                    variant="secondary"
                    onClick={handleCloseModal}
                    disabled={processing}
                  >
                    Cancel
                  </Button>

                  <Button
                    variant="primary"
                    onClick={handleConfirmModal}
                    disabled={processing}
                  >
                    {processing && (
                      <span className="ms-2 spinner-border spinner-border-sm" />
                    )}
                    {!processing &&
                      (modalMode === 'publish' ? 'Publish' : 'Unpublish')}
                  </Button>
                </Modal.Footer>
              </Modal>
            </div>
          )}
        </Tab>
        <Tab eventKey="selfAssessments" title="Self Assessments">
          <div>
            {selfAssessments.length === 0 ? (
              <div
                className="d-flex justify-content-center align-items-center flex-column"
                style={{ height: '400px' }}
              >
                <p>No assessments to show.</p>
                <p>
                  Go to the <a href="/self-assessments">Questionnaires</a> page
                  to start a self assessment.
                </p>
              </div>
            ) : (
              <Table
                striped
                bordered
                className="table-cell-vertical-align-center"
              >
                <thead>
                  <tr>
                    <th>Assessment</th>
                    <th>Status</th>
                    <th>Result</th>
                    <th>Completed</th>
                    <th>Published</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {selfAssessments.map((assessment) => (
                    <tr key={assessment.assessment_id}>
                      <td>
                        {assessment.template_title}{' '}
                        {!assessment.is_report_published &&
                        assessment.is_self_assessment ? (
                          <span className="text-muted">(Unpublished)</span>
                        ) : null}
                      </td>
                      <td>
                        {getStatusColumn(assessment.status)}{' '}
                        {assessment.status === 'completed' && (
                          <Button
                            variant="link"
                            className="p-0 ms-2"
                            size="sm"
                            onClick={() =>
                              handleViewAssessment(assessment.assessment_id)
                            }
                          >
                            Report
                          </Button>
                        )}
                      </td>
                      <td>
                        <AssessmentScoreBar
                          value={getTotalScorePercentage(
                            assessment.total_score,
                            assessment.max_score,
                          )}
                        />
                      </td>
                      <td>
                        {assessment.completed_at
                          ? new Date(
                              assessment.completed_at,
                            ).toLocaleDateString()
                          : '-'}
                      </td>

                      {/* ⭐ Published column */}
                      <td>
                        {assessment.is_self_assessment &&
                        assessment.status === 'completed' ? (
                          <Form.Check
                            type="switch"
                            label="Published"
                            id="publish-switch"
                            checked={!!assessment.is_report_published}
                            onChange={() =>
                              togglePublishStatus(
                                assessment.assessment_id,
                                !!assessment.is_report_published,
                              )
                            }
                          />
                        ) : null}
                        {/* {assessment.is_report_published ? (
                      <div className="d-flex align-items-center">
                        Yes{' '}
                        <Button
                          size="sm"
                          variant="link"
                          className="ms-2"
                          onClick={() =>
                            handleOpenUnpublishModal(assessment.id)
                          }
                        >
                          Unpublish
                        </Button>
                      </div>
                    ) : (
                      <div className="d-flex align-items-center">
                        No{' '}
                        <Button
                          size="sm"
                          variant="link"
                          className="ms-2"
                          onClick={() => handleOpenPublishModal(assessment.id)}
                        >
                          Publish
                        </Button>
                      </div>
                    )} */}
                      </td>

                      {/* ⭐ Actions column */}
                      <td>
                        {assessment.is_report_published && (
                          <>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              className="ms-2 mb-1 editor-icon-button"
                              title="Copy public url"
                              onClick={() =>
                                handleCopyPublicUrl(assessment.assessment_id)
                              }
                            >
                              <i className="bi bi-copy me-1"></i>
                              Copy URL
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              className="ms-2 mb-1 editor-icon-button"
                              title="View embed code"
                              onClick={() =>
                                handleOpenBadgeModal(assessment.assessment_id)
                              }
                            >
                              <i className="bi bi-code-square me-1"></i>
                              Embed
                            </Button>
                          </>
                        )}
                        {(assessment.status === 'created' ||
                          assessment.status === 'started') && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() =>
                              (() => {
                                tracking.log('UI', 'assessment_start', {
                                  assessmentId: assessment.assessment_id,
                                  action:
                                    assessment.status === 'created'
                                      ? 'start'
                                      : 'continue',
                                  source: 'self',
                                });
                                navigate(
                                  `/assessment/${assessment.assessment_id}`,
                                );
                              })()
                            }
                          >
                            {assessment.status === 'created'
                              ? 'Start'
                              : 'Continue'}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            {/* ⭐ Shared Publish/Unpublish Confirmation Modal */}
            <Modal show={showConfirmModal} onHide={handleCloseModal} centered>
              <Modal.Header closeButton>
                <Modal.Title>
                  {modalMode === 'publish'
                    ? 'Publish Report'
                    : 'Unpublish Report'}
                </Modal.Title>
              </Modal.Header>

              <Modal.Body>
                {modalMode === 'publish'
                  ? 'Are you sure you want to publish this report? Once published, it will be publicly accessible.'
                  : 'Are you sure you want to unpublish this report? It will no longer be publicly accessible.'}
              </Modal.Body>

              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={handleCloseModal}
                  disabled={processing}
                >
                  Cancel
                </Button>

                <Button
                  variant="primary"
                  onClick={handleConfirmModal}
                  disabled={processing}
                >
                  {processing && (
                    <span className="ms-2 spinner-border spinner-border-sm" />
                  )}
                  {!processing &&
                    (modalMode === 'publish' ? 'Publish' : 'Unpublish')}
                </Button>
              </Modal.Footer>
            </Modal>
          </div>
        </Tab>
      </Tabs>

      <SupplierBadgeEmbedModal
        show={showBadgeModal}
        onHide={() => setShowBadgeModal(false)}
        assessmentId={embedAssessmentId}
        reportId={embedReportId}
      />

      <ToastContainer position={gToastPosition} className="p-3">
        <Toast
          onClose={() => setToastNotification(undefined)}
          show={!isUndefined(toastNotification)}
          delay={3000}
          autohide
          bg={gToastSentiment}
        >
          <Toast.Body>{toastNotification}</Toast.Body>
        </Toast>
      </ToastContainer>
    </Container>
  );
};

export default SupplierAssessments;
